import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { ShoppingCart, User, LogOut, Menu, Home } from 'lucide-react';

const ClientLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/order" className="flex items-center">
              <img 
                src="https://www.munchdmeals.com/cdn/shop/files/png-15.png?v=1697032222" 
                alt="Munch'd Meals" 
                className="h-10"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link 
                to="/order" 
                className={`text-sm font-medium transition-colors ${
                  isActive('/order') ? 'text-green-600' : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="nav-order-link"
              >
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  Place Order
                </div>
              </Link>
              <Link 
                to="/account" 
                className={`text-sm font-medium transition-colors ${
                  isActive('/account') ? 'text-green-600' : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="nav-account-link"
              >
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  My Account
                </div>
              </Link>
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-slate-900">{user?.organisation_name || user?.name}</p>
                <p className="text-xs text-slate-500 font-mono">{user?.username}</p>
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-button">
                    <div className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-green-600" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 sm:hidden">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500 font-mono">{user?.username}</p>
                  </div>
                  <DropdownMenuSeparator className="sm:hidden" />
                  <DropdownMenuItem asChild>
                    <Link to="/order" className="cursor-pointer" data-testid="menu-order-link">
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Place Order
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/account" className="cursor-pointer" data-testid="menu-account-link">
                      <User className="w-4 h-4 mr-2" />
                      My Account
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout} 
                    className="text-red-600 cursor-pointer"
                    data-testid="menu-logout-button"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © 2026 Munch'd Meals. All rights reserved.
            </p>
            <p className="text-sm text-slate-500">
              Need help? <a href="mailto:hello@munchdmeals.com" className="text-green-600 hover:text-green-700">hello@munchdmeals.com</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientLayout;
